import { useMemo } from "react"
import type { CarrotPlanFormStep } from "../CarrotPlanFormTypes"
import type { CarrotPlanFormOption, MenuItemOptionFields } from "./useCarrotPlanMappings"
import { getOnlyRequiredMenuItems } from "../utils/autocompleteTransformations"

export const TOTAL_STEP_SLOTS = 5

/**
 * Arranges step rows into the form's fixed slots with required items pinned to the bottom.
 *
 * Layout: user-added on top, empty slots in the middle, required at the bottom.
 * Overflow: required is preserved; user-added is sliced from the end to fit remaining slots.
 * Final `id` and `stepIndex` are renumbered to match each row's slot position.
 */
export function arrangeStepRows(
  userAddedSteps: CarrotPlanFormStep[],
  requiredSteps: CarrotPlanFormStep[]
): CarrotPlanFormStep[] {
  const cappedRequired = requiredSteps.slice(0, TOTAL_STEP_SLOTS)
  const userSlotsAvailable = Math.max(0, TOTAL_STEP_SLOTS - cappedRequired.length)
  const cappedUserAdded = userAddedSteps.slice(0, userSlotsAvailable)
  const emptyCount = TOTAL_STEP_SLOTS - cappedUserAdded.length - cappedRequired.length
  const emptySlots: CarrotPlanFormStep[] = Array.from({ length: emptyCount }, () => ({
    id: "",
    stepIndex: 0
  }))

  return [...cappedUserAdded, ...emptySlots, ...cappedRequired].map((step, i) => ({
    ...step,
    id: `stepId-${i}`,
    stepIndex: i
  }))
}

/**
 * Computes initial step values from required menu items (excluding DISABLE),
 * capped at TOTAL_STEP_SLOTS. Required items render at the bottom; remaining
 * slots above are left empty for the user to fill.
 */
export function useInitialCarrotPlanSteps(
  initialMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
): CarrotPlanFormStep[] {
  return useMemo(() => {
    const requiredSteps = getOnlyRequiredMenuItems(initialMenuItems).map((item) => ({
      id: "",
      stepIndex: 0,
      menuItem: item,
      url: item.optionFields?.defaultUrl ?? null
    }))
    return arrangeStepRows([], requiredSteps)
  }, [initialMenuItems])
}
